/**
 * FAQs for the agent-usage landing pages (/claude-code-usage, /codex-usage,
 * /opencode-usage), exported separately so routes.tsx can build the FAQPage
 * JSON-LD without pulling the page chunk into the route table.
 * Voice: plain and technical, see docs/design-system/copy.md.
 */
type AgentFaqConfig = {
  agent: string // 'Claude Code'
  short: string // 'Claude' as in "my Claude session"
  cli: string // 'npx langwatch claude'
  how: string // agent-specific setup mechanics for Q1
  bundled: string // what the no-invoice plan is called for this agent
  others: string // the sibling agents named in Q2
}

function agentUsageFaq(c: AgentFaqConfig): { q: string; a: string }[] {
  return [
    {
      q: `How do I track my ${c.agent} usage?`,
      a: `Run ${c.cli}. ${c.how} Every session lands as a trace: model turns, tool calls, tokens by class, and theoretical cost. ccusage-style local log readers see one machine; the OpenTelemetry route follows you across machines and survives local log cleanup.`,
    },
    {
      q: `Does it work with ${c.others}?`,
      a: 'Yes. Each agent exports through the same OTLP endpoint, so everything lands in one store with the same token, cost, and trace treatment.',
    },
    {
      q: 'What does the "Bundled" cost label mean?',
      a: `${c.bundled} LangWatch marks those sessions Bundled and computes a theoretical total at API list prices, per token class. That is the number behind "what would this have cost on the API".`,
    },
    {
      q: 'Are cached tokens counted?',
      a: 'Cache read and cache write are separate token classes on every span, priced at their own rates. In long agent sessions cache reads are most of the volume, so a tracker that lumps them into input gets both the total and the cost wrong.',
    },
    {
      q: `What if I accidentally paste any secret keys on my ${c.short} session?`,
      a: 'API secrets are automatically redacted and never stored on the LangWatch side. PII is redacted the same way, so personal information does not end up in the trace. For tighter control, enterprise policies and self-hosted setups are available.',
    },
  ]
}

export const claudeCodeUsageFaq = agentUsageFaq({
  agent: 'Claude Code',
  short: 'Claude',
  cli: 'npx langwatch claude',
  how: 'It configures Claude Code’s native OpenTelemetry export to LangWatch.',
  bundled: 'Sessions on a Claude subscription (Pro, Max, Team) have no per-token invoice.',
  others: 'Codex, OpenCode, Copilot, Cursor, and Pi',
})

export const codexUsageFaq = agentUsageFaq({
  agent: 'Codex',
  short: 'Codex',
  cli: 'npx langwatch codex',
  how: 'It configures the OpenTelemetry exporters Codex ships in config.toml to point at LangWatch.',
  bundled: 'Sessions on a ChatGPT plan (Plus, Pro, Team) have no per-token invoice.',
  others: 'Claude Code, OpenCode, Copilot, Cursor, and Pi',
})

export const openCodeUsageFaq = agentUsageFaq({
  agent: 'OpenCode',
  short: 'OpenCode',
  cli: 'npx langwatch opencode',
  how: 'It configures OpenCode’s OpenTelemetry export to LangWatch, whichever provider your models run on.',
  bundled:
    'Sessions on a provider subscription you signed into (a ChatGPT plan, Claude Pro or Max) have no per-token invoice.',
  others: 'Claude Code, Codex, Copilot, Cursor, and Pi',
})
